import React, { useEffect, useState } from 'react';
import {
  Box,
  Button as MuiButton,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { HelpCircle, Plus, Search } from 'lucide-react';
import { toast } from '../../../../ui/toast';
import Pagination from '../../../../ui/components/Common/DataTable/Paginaion';
import { CustomSelect } from '../../../../ui/custom_react_select/custom_select';
import { TextInput } from '../../../../ui/input/text_input/text_input';
import TableSkeletonLoader from '../../../../ui/TableSkeletonLoader';
import {
  createPreScreenQuestion,
  searchPreScreenQuestions,
} from '../../../../utils/api_service/pre_screen_api';
import {
  PreScreenQuestionBank,
  PreScreenQuestionType,
} from '../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { PreScreenQuestion } from '../PreScreenQuestion/index';

export const questionTypeOptions: SelectOptions[] = [
  {
    label: 'Numeric',
    value: 'numeric',
  },
  {
    label: 'Multiple Choice',
    value: 'multiple-choice',
  },
  {
    label: 'Video',
    value: 'video',
  },
];

export const PreScreenBankSearch = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [questionType, setQuestionType] =
    useState<PreScreenQuestionType | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalQuestions, setTotalQuestions] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [questionsList, setQuestionsList] = useState<PreScreenQuestionBank[]>(
    [],
  );
  const [newQuestion, setNewQuestion] = useState<boolean>(false);
  const [newQuestionType, setNewQuestionType] =
    useState<PreScreenQuestionType>('numeric');
  const [newQuestionText, setNewQuestionText] = useState<string>('');
  const [newQuestionDefault, setNewQuestionDefault] = useState<boolean>(false);
  const [choices, setChoices] = useState<string[]>(['']);
  const rowsPerPage = 10;

  useEffect(() => {
    setPageNumber(1);
  }, [searchTerm, questionType]);

  useEffect(() => {
    const debounceSearchTerm = setTimeout(async () => {
      setLoading(true);

      try {
        const response = await searchPreScreenQuestions(
          searchTerm,
          questionType as PreScreenQuestionType,
          pageNumber,
          rowsPerPage,
        );
        if (!response.ok) {
          throw new Error();
        }
        const result = await response.json();
        setQuestionsList(result.questions);
        setPageNumber(result.page);
        setTotalPages(Math.ceil(result.total / rowsPerPage));
        setTotalQuestions(result.total);
      } catch (error) {
        toast.error('Error fetching questions', { position: 'bottom-center' });
      } finally {
        setLoading(false);
      }
    }, 1000);

    return () => clearTimeout(debounceSearchTerm);
  }, [searchTerm, questionType, pageNumber]);

  const handleChoiceChange = (index: number, value: string) => {
    const newChoices = [...choices];
    newChoices[index] = value;
    setChoices(newChoices);
  };

  const validateQuestionData = (
    questionText: string,
    validatedType: PreScreenQuestionType,
    choicesList: string[],
    defaultQuestion: boolean,
  ) => {
    const trimmedQuestionText = questionText.trim();
    const filteredChoices = choicesList.filter((choice) => choice.trim() !== '');

    if (!trimmedQuestionText) {
      toast.error('Question text cannot be empty', {
        position: 'bottom-center',
      });
      return null;
    }

    if (validatedType === 'multiple-choice' && filteredChoices.length === 0) {
      toast.error('Choices cannot be empty for a multiple-choice question', {
        position: 'bottom-center',
      });
      return null;
    }

    const questionObj: PreScreenQuestionBank = {
      question: trimmedQuestionText,
      questionType: validatedType,
      choices: validatedType === 'multiple-choice' ? filteredChoices : [],
      defaultQuestion: defaultQuestion,
    };

    return questionObj;
  };

  const handleAddQuestion = async () => {
    const newQuestionData: PreScreenQuestionBank | null = validateQuestionData(
      newQuestionText,
      newQuestionType,
      choices,
      newQuestionDefault,
    );

    if (!newQuestionData) return;

    try {
      const response = await createPreScreenQuestion(newQuestionData);
      if (!response.ok) {
        throw new Error();
      }

      const result = await response.json();

      setQuestionsList((prevQuestions) => [result, ...prevQuestions]);

      toast.success('Question Added Successfully', {
        position: 'bottom-center',
      });

      setNewQuestionText('');
      setChoices(['']);
      setNewQuestion(false);
    } catch (error) {
      toast.error('Error adding question', { position: 'bottom-center' });
    }
  };

  const handleDeleteQuestion = (questionId: string) => {
    setQuestionsList(
      questionsList.filter((question) => question.id !== questionId),
    );
  };

  const handleEditQuestion = (editQuestion: PreScreenQuestionBank) => {
    setQuestionsList(
      questionsList.map((question) =>
        question.id === editQuestion.id ? editQuestion : question,
      ),
    );
  };

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Paper
        variant='outlined'
        sx={{
          px: 2,
          py: 1,
          borderRadius: 2,
          textAlign: 'center',
          bgcolor: 'rgba(16, 185, 129, 0.05)',
          borderColor: 'rgba(16, 185, 129, 0.2)',
        }}
      >
        <Typography variant='caption' fontWeight={500} color='text.secondary'>
          You can view all the pre-screen questions that are used in all assessments.
        </Typography>
      </Paper>

      <Paper
        variant='outlined'
        sx={{
          p: 1.5,
          borderRadius: 2,
          borderColor: 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          alignItems={{ lg: 'center' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextInput
              name='searchQuestions'
              value={searchTerm}
              type='text'
              placeholder='Search questions'
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: 280 } }}>
            <CustomSelect
              name='questionType'
              placeholder='Select Question Type'
              value={questionTypeOptions.find(
                (option) => option.value === questionType,
              )}
              options={questionTypeOptions}
              onChange={(selectedOption: SelectOptions | null) =>
                setQuestionType(
                  selectedOption
                    ? (selectedOption.value as 'numeric' | 'multiple-choice')
                    : null,
                )
              }
              clearable
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: 'auto' } }}>
            <MuiButton
              variant='outlined'
              onClick={() => setNewQuestion(!newQuestion)}
              startIcon={<Plus size={16} />}
              sx={{
                height: 40,
                borderColor: '#cbd5e1',
                color: '#334155',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                width: { xs: '100%', lg: 'auto' },
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: '#94a3b8',
                  bgcolor: '#f8fafc',
                },
              }}
            >
              New Question
            </MuiButton>
          </Box>
        </Stack>
      </Paper>

      {newQuestion && (
        <Paper
          variant='outlined'
          sx={{
            p: 2,
            borderRadius: 2,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography variant='subtitle2' fontWeight={700} color='text.primary'>
              Create Pre-screen Question
            </Typography>
            <Chip
              label='Draft'
              size='small'
              sx={{
                bgcolor: '#ecfdf5',
                color: '#047857',
                fontWeight: 600,
                fontSize: '0.6875rem',
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            }}
          >
            <Box>
              <TextInput
                label='Question'
                name='question'
                value={newQuestionText}
                type='text'
                onChange={(e) => setNewQuestionText(e.target.value)}
              />
            </Box>
            <Box>
              <CustomSelect
                label='Question Type'
                name='questionType'
                value={questionTypeOptions.find(
                  (option) => option.value === newQuestionType,
                )}
                options={questionTypeOptions}
                onChange={(selectedOption: SelectOptions) =>
                  setNewQuestionType(
                    selectedOption.value as PreScreenQuestionType,
                  )
                }
              />
            </Box>
          </Box>

          {newQuestionType === 'multiple-choice' && (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              <Typography
                variant='caption'
                fontWeight={700}
                textTransform='uppercase'
                letterSpacing='0.04em'
                color='text.secondary'
              >
                Choices
              </Typography>
              {choices.map((choice, index) => (
                <Stack key={index} direction='row' spacing={1} alignItems='center'>
                  <Box sx={{ flex: 1 }}>
                    <TextInput
                      name='choice'
                      value={choice}
                      type='text'
                      onChange={(e) =>
                        handleChoiceChange(index, e.target.value)
                      }
                    />
                  </Box>
                  {index === choices.length - 1 && (
                    <MuiButton
                      variant='outlined'
                      size='small'
                      onClick={() => setChoices([...choices, ''])}
                      sx={{
                        height: 38,
                        borderColor: '#cbd5e1',
                        color: '#334155',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 1.5,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Add Choice
                    </MuiButton>
                  )}
                </Stack>
              ))}
            </Stack>
          )}

          <Box
            sx={{
              mt: 2,
              pt: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size='small'
                  checked={newQuestionDefault}
                  onChange={() => setNewQuestionDefault(!newQuestionDefault)}
                  sx={{ color: '#10b981', '&.Mui-checked': { color: '#10b981' } }}
                />
              }
              label={
                <Typography variant='caption' fontWeight={500} color='text.secondary'>
                  Set as default
                </Typography>
              }
            />
            <MuiButton
              variant='contained'
              onClick={handleAddQuestion}
              sx={{
                bgcolor: '#10b981',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#059669',
                  boxShadow: 'none',
                },
              }}
            >
              Add Question
            </MuiButton>
          </Box>
        </Paper>
      )}

      <Paper
        variant='outlined'
        sx={{
          borderRadius: 2,
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ p: 2 }}>
            <TableSkeletonLoader numRows={9} height={12} />
          </Box>
        ) : questionsList.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 220,
              p: 4,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                mb: 1.5,
              }}
            >
              <HelpCircle size={24} />
            </Box>
            <Typography variant='subtitle2' fontWeight={700} color='text.primary'>
              No pre-screen questions found
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5 }}>
              Try adjusting your search or create a new pre-screen question.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 1 }}>
            {questionsList.map((question, indexNum) => (
              <PreScreenQuestion
                question={question}
                deleteQuestion={handleDeleteQuestion}
                editQuestion={handleEditQuestion}
                key={indexNum}
                validateQuestion={validateQuestionData}
              />
            ))}
          </Box>
        )}
      </Paper>

      {!loading && questionsList.length > 0 && (
        <Paper
          variant='outlined'
          sx={{
            borderRadius: 2,
            borderColor: 'divider',
          }}
        >
          <Pagination
            currentPage={pageNumber}
            totalPages={totalPages}
            totalRecords={totalQuestions}
            rowsPerPage={rowsPerPage}
            handlePageChange={setPageNumber}
          />
        </Paper>
      )}
    </Stack>
  );
};
